
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SEO } from '../SEO';
import { MedicalCaseStudies } from '../MedicalCaseStudies';
import { MedicalBenefits } from '../MedicalBenefits';
import {
  ShieldCheck, Server, Lock, CheckCircle2,
  Quote, Minus, Plus, Cpu, Calculator, ArrowRight, MessageSquare, X, Check, Loader2, Activity, HeartPulse, Stethoscope
} from 'lucide-react';
import { Page } from '../../types';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';

const MedicalTerminal = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStep((prev) => (prev + 1) % 5), 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 bg-[#0F172A] mt-2 h-[350px] flex flex-col">
      <div className="bg-gray-800 px-4 py-2 flex items-center gap-2 border-b border-gray-700 shrink-0">
        <div className="w-3 h-3 rounded-full bg-red-500"></div>
        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
        <div className="w-3 h-3 rounded-full bg-green-500"></div>
        <span className="ml-4 text-xs font-mono text-gray-400">{t('medicine.terminal.analysis')}</span>
      </div>
      <div className="p-6 font-mono text-xs md:text-sm leading-loose overflow-hidden relative flex-1 text-gray-300">
        <div className="flex gap-2"><span className="text-blue-400">root@rrl-med:~$</span><span className="text-white">{t('medicine.terminal.prompt')}</span></div>
        <div className={`transition-opacity duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="mt-2 text-gray-500">{t('medicine.terminal.model_loading')}</div>
        </div>
        <div className={`transition-opacity duration-500 ${step >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-yellow-400 mt-4">{t('medicine.terminal.processing')}</div>
          <div className="pl-4 border-l border-gray-700 italic opacity-70 my-2">{t('medicine.terminal.patient_input')}</div>
        </div>
        <div className={`transition-opacity duration-500 ${step >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-green-400 mt-4">{t('medicine.terminal.draft_proposal')}</div>
          <div className="bg-blue-900/20 p-4 rounded border border-blue-500/20 mt-2 text-blue-200">{"{ \"icd_10\": [\"R51\", \"G44.2\"] }"}</div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent pointer-events-none opacity-50"></div>
      </div>
      <div className="bg-gray-800 px-4 py-1 flex justify-between text-[10px] text-gray-500 border-t border-gray-700 shrink-0">
        <span>{t('medicine.terminal.mem')}</span><span className="text-green-400">{t('medicine.terminal.secure_enclave')}</span>
      </div>
    </div>
  );
};

interface MedicinePageProps { onNavigate?: (page: Page) => void; }

export const MedicinePage: React.FC<MedicinePageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const subSectors = [
    { title: t('medicine.sectors.items.01.title'), id: "01", desc: t('medicine.sectors.items.01.desc'), highlight: "GP" },
    { title: t('medicine.sectors.items.02.title'), id: "02", desc: t('medicine.sectors.items.02.desc'), highlight: "Imaging" },
    { title: t('medicine.sectors.items.03.title'), id: "03", desc: t('medicine.sectors.items.03.desc'), highlight: "Pharmacy" },
    { title: t('medicine.sectors.items.04.title'), id: "04", desc: t('medicine.sectors.items.04.desc'), highlight: "EyeCare" },
    { title: t('medicine.sectors.items.05.title'), id: "05", desc: t('medicine.sectors.items.05.desc'), highlight: "Hospital" },
    { title: t('medicine.sectors.items.06.title'), id: "06", desc: t('medicine.sectors.items.06.desc'), highlight: "Lab" },
    { title: t('medicine.sectors.items.07.title'), id: "07", desc: t('medicine.sectors.items.07.desc'), highlight: "Care" },
    { title: t('medicine.sectors.items.08.title'), id: "08", desc: t('medicine.sectors.items.08.desc'), highlight: "Custom", variant: 'dark' }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      <SEO
        title={t('medicine.hero.title_line1') + ' ' + t('medicine.hero.title_line2')}
        description={t('medicine.hero.subtitle')}
        keywords="Medizin KI, Arztsoftware Automatisierung, Gesundheitswesen Digitalisierung"
      />
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="pt-4">
              <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-blue-100">{t('medicine.hero.tagline')}</div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mb-8">{t('medicine.hero.title_line1')} <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">{t('medicine.hero.title_line2')}</span></h1>
              <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-lg">{t('medicine.hero.subtitle')}</p>
              <div className="flex flex-wrap gap-4 mb-12">
                <button onClick={() => setIsModalOpen(true)} className="bg-blue-600 text-white px-6 py-4 font-bold uppercase tracking-widest text-xs hover:bg-blue-700 transition-all shadow-lg rounded-sm flex items-center gap-3">{t('medicine.hero.cta_strategy')}</button>
                <button onClick={() => onNavigate && onNavigate('roi')} className="bg-transparent text-gray-900 border border-gray-200 px-6 py-4 font-bold uppercase tracking-widest text-xs hover:border-blue-600 transition-all rounded-sm flex items-center gap-2"><Calculator size={16} /> {t('medicine.hero.cta_roi')}</button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}><MedicalTerminal /></Reveal>
        </div>
      </section>

      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">{t('medicine.sectors.title').split(' ')[0]}<br />{t('medicine.sectors.title').split(' ')[1] || ''}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
            {subSectors.map((sector, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div
                  onClick={() => setIsModalOpen(true)}
                  className={`group p-12 border-b border-r border-gray-200 transition-all duration-500 min-h-[320px] flex flex-col justify-between relative cursor-pointer overflow-hidden h-full ${sector.variant === 'dark' ? 'bg-[#0B1121] text-white border-gray-800' : 'bg-white hover:bg-blue-50/30'}`}
                >
                  <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><span className={`${sector.variant === 'dark' ? 'text-clever-teal' : 'text-blue-600'} text-xs font-bold uppercase tracking-widest`}>{sector.highlight}</span></div>
                  <div className={`text-xs font-mono mb-8 ${sector.variant === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>// {sector.id}</div>
                  <div>
                    <h3 className={`text-3xl font-bold mb-6 group-hover:translate-x-2 transition-transform ${sector.variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{sector.title}</h3>
                    <p className={`leading-relaxed text-lg ${sector.variant === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{sector.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MedicalBenefits />

      <section className="py-40 bg-[#0B1121] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-20">
              <div className="inline-block px-4 py-2 bg-blue-900/30 text-blue-300 text-xs font-bold uppercase tracking-[0.2em] mb-6 border border-blue-500/30">{t('medicine.consultant.tagline')}</div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('medicine.consultant.title')}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">{t('medicine.consultant.subtitle')}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <SmartConsultant
              context="medicine"
              title={t('medicine.consultant.bot_title')}
              subtitle={t('medicine.consultant.bot_subtitle')}
              suggestions={t('medicine.consultant.suggestions', { returnObjects: true }) as string[]}
            />
          </Reveal>
        </div>
      </section>

      <MedicalCaseStudies />

      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Medicine" />
    </div>
  );
};
